import type { HoverContent } from "./HoverCard";
import { segmentWidths } from "./scale";

export interface Segment {
  value: number;
  color: string;
  label?: string;
  /** The designed hover card. Takes precedence over `label`, so a segment never carries both the
   *  browser's tooltip and ours. */
  hover?: HoverContent;
}

/**
 * A horizontal proportion bar — the shape eleven of Stats' panels are built from.
 *
 * Coverage's tier and tag rows, and Recall's answer-button volumes. No axis, no ticks, no scale:
 * the segments *are* the scale, which is the clearest single reason this screen took no chart
 * library. Widths come from `segmentWidths`, which sums to exactly 100 — rounding each segment
 * independently leaves a hairline gap at the end of the track that reads as a fifth category
 * nobody defined.
 *
 * **`hatched` is a row with no universe**, not a row with nothing in it. Over 50k and terms with no
 * rank have no denominator — the frequency list stops at 99,999, which is where the file ends
 * rather than where the language does — so the row draws a hatch and reports `—` for absent and
 * cover instead of a percentage that would read as a near-complete tier beside Top 50k's 2.5%.
 */
export function StackedBar({
  segments,
  height = 10,
  hatched = false,
  className = "",
  onEnter,
  onLeave,
}: {
  segments: Segment[];
  height?: number;
  hatched?: boolean;
  className?: string;
  /** From `useChartHover`, for segments carrying `hover`. */
  onEnter?: (event: { currentTarget: Element }, content: HoverContent) => void;
  onLeave?: () => void;
}) {
  const widths = segmentWidths(segments.map((s) => s.value));
  return (
    <div
      // 3px, measured off the frames' tier rows. `rounded-chip` was invented here and Tailwind
      // never generated it, so every bar on this surface was a hard rectangle.
      // `checks/` hook: a bar with no width left to it is the phone failure this surface actually
      // had, and it is invisible to everything except a measurement of this element.
      data-check="chart.stacked-bar"
      className={`flex overflow-hidden rounded-[var(--r-2)] ${className}`}
      style={{
        height,
        background: hatched ? "var(--cover-nouniverse)" : "var(--line)",
      }}
      role="presentation"
    >
      {!hatched &&
        segments.map((segment, index) => (
          <span
            key={index}
            title={segment.hover ? undefined : segment.label}
            // No rounding on a segment: it is a slice of one track, and the hover ring is drawn
            // outside the box, so the neighbours are unmoved.
            className={segment.hover && onEnter ? "chart-mark" : ""}
            style={{ width: `${widths[index]}%`, background: segment.color }}
            onMouseEnter={
              segment.hover && onEnter
                ? (event) => onEnter(event, segment.hover as HoverContent)
                : undefined
            }
            onMouseLeave={segment.hover && onEnter ? onLeave : undefined}
          />
        ))}
    </div>
  );
}

/**
 * The legend the bars above share.
 *
 * One component so the four segment names and their colours are stated once. Coverage draws it at
 * the head of both tables, and a second hand-written copy is how the tag bars would end up calling
 * `--cover-new` something other than "new".
 */
export function SegmentLegend({ items }: { items: { label: string; color: string }[] }) {
  return (
    <div className="flex flex-wrap items-center gap-[10px]">
      {items.map(({ label, color }) => (
        <span key={label} className="flex items-center gap-[5px] text-[14px]/[1.4] text-ink-2">
          <span
            aria-hidden
            className="h-[11px] w-[11px] rounded-[var(--r-2)]"
            style={{ background: color }}
          />
          {label}
        </span>
      ))}
    </div>
  );
}
